import { useQuery } from '@apollo/client';
import { IInputBoxProps, TextBox } from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import { setServiceAddress, setServiceZoneTimezone } from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { Phrase } from 'components/Phrase';
import { ReactComponent as InputPinSvg } from 'components/svg/InputPin.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CountryCode,
  ICarouselItemServiceAreaBottomProps,
  IGetServiceZoneFromZipCodeResponse,
  IGetServiceZoneFromZipCodeVariables,
  IState,
} from 'typings';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';
import {
  postCodeRegexp,
  postalCodeInputRegexp,
  postalCodeRegexp,
  zipCodeInputRegexp,
  zipCodeRegexp,
} from 'utils/zipCodeRegexps';

import { Buttons, Container, FormGroup } from './styles';

const getServiceZoneFromZipCodeQuery = loader(
  '../../queries/getServiceZoneFromZipCode.graphql',
);

/**
 * NOTE: This component is also use for ServiceType.SALES, but that will be
 * deprecated in the future.
 */
export const CAROUSEL_ITEM_ID = CarouselItemId.ServiceAreaRepair;

export const CarouselItemServiceAreaBottom = ({
  destinationItemId = CarouselItemId.SalesAddress,
  successPhraseId = 'SERVICE_AREA.SUCCESS.HTML',
}: ICarouselItemServiceAreaBottomProps) => {
  const dispatch = useThunkDispatch();
  const {
    countryCodes = [CountryCode.US],
    flow,
    serviceType,
    zipCode = '',
  } = useSelector((state: IState) => ({
    countryCodes: state.client.countryCodes,
    flow: state.client.flow,
    serviceType: state.userInput.serviceType,
    zipCode: state.userInput.address.zipCode,
  }));

  const flowCarouselItem = tryGetFlowCarouselItem(flow, CAROUSEL_ITEM_ID);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const zipCodeRef = useRef(zipCode);
  const { buttonType, enterpriseEnabled, enterpriseVersion } = useTheme();

  const bottomButtonFormat = flow?.buttonType ?? buttonType;

  if (flowCarouselItem) {
    destinationItemId = flowCarouselItem.options.destinations.next;
  }

  const isZipCodeValid = useMemo(() => {
    return countryCodes.some((countryCode) => {
      switch (countryCode) {
        case CountryCode.AU:
          return postCodeRegexp.test(zipCode);
        case CountryCode.CA:
          return postalCodeRegexp.test(zipCode);
        case CountryCode.US:
        default:
          return zipCodeRegexp.test(zipCode);
      }
    });
  }, [countryCodes, zipCode]);

  const { data: serviceZoneData, loading: isLoading } = useQuery<
    IGetServiceZoneFromZipCodeResponse,
    IGetServiceZoneFromZipCodeVariables
  >(getServiceZoneFromZipCodeQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const locality = data.getServiceZoneFromZipCode?.locality;
      const serviceZoneTimezone =
        data.getServiceZoneFromZipCode?.serviceZoneTimezone;

      if (locality) {
        const { city, countryCode, state } = locality;

        dispatch(setServiceAddress({ city, countryCode, state }));
      }

      if (serviceZoneTimezone) {
        dispatch(setServiceZoneTimezone(serviceZoneTimezone));
      }
    },
    skip: !isZipCodeValid,
    variables: {
      enterpriseVersion: enterpriseEnabled ? enterpriseVersion : undefined,
      serviceType,
      zipCode,
    },
  });

  const isInServiceZone: boolean | undefined =
    serviceZoneData?.getServiceZoneFromZipCode?.isInServiceZone;

  const inputOptions = useMemo(() => {
    const auOptions: Partial<IInputBoxProps> = {
      cleaveOptions: { blocks: [4], numericOnly: true },
      inputMode: 'numeric',
      maxLength: 4,
      pattern: `${zipCodeInputRegexp}`,
    };
    const caOptions: Partial<IInputBoxProps> = {
      cleaveOptions: {
        blocks: [3, 3],
        numericOnly: false,
        uppercase: true,
      },
      inputMode: 'text',
      maxLength: 7,
      pattern: `${postalCodeInputRegexp}`,
    };

    const initialOptions: Partial<IInputBoxProps> = {
      cleaveOptions: {
        blocks: [7],
        numericOnly: false,
        uppercase: true,
      },
      inputMode: 'text',
      maxLength: 7,
    };

    const usOptions: Partial<IInputBoxProps> = {
      cleaveOptions: { blocks: [5], numericOnly: true },
      inputMode: 'numeric',
      maxLength: 5,
      pattern: `${zipCodeInputRegexp}`,
    };

    // If there is only one countryCode, use that country's options
    if (countryCodes.length === 1) {
      const [countryCode] = countryCodes;

      switch (countryCode) {
        case CountryCode.AU:
          return auOptions;
        case CountryCode.CA:
          return caOptions;
        case CountryCode.US:
        default:
          return usOptions;
      }
    }

    // If multiple country codes are supported, and we don't have any zipCode
    // input yet, default to allowing anything
    if (!zipCode) {
      return initialOptions;
    }

    // If multiple country codes are supported, and if the first character is a
    // digit, assume US options should be used. Otherwise, use Canadian options.
    const isFirstCharacterDigit = /^\d/.test(zipCode);

    return isFirstCharacterDigit ? usOptions : caOptions;
  }, [countryCodes, zipCode]);

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setServiceAddress({ zipCode: event.currentTarget.value }));
  };

  const errorMessage = !isLoading && isInServiceZone === false && (
    <Phrase id="SERVICE_AREA.ERROR.HTML" />
  );

  const successMessage = !isLoading && isInServiceZone && (
    <Phrase id={successPhraseId} />
  );

  useEffect(() => {
    if (zipCodeRef.current !== zipCode) {
      inputRef.current?.focus();
    }

    zipCodeRef.current = zipCode;
  }, [zipCode]);

  return (
    <FormGroup>
      <Container>
        <TextBox
          autoComplete="postal-code"
          description={Phrase({ id: 'SERVICE_AREA.ZIP_CODE.DESCRIPTION' })}
          errorMessage={errorMessage}
          inputRef={inputRef}
          isLoading={isLoading}
          label={Phrase({ id: 'SERVICE_AREA.ZIP_CODE.LABEL' })}
          leftIcon={InputPinSvg}
          name="zip"
          onChange={handleChange}
          successMessage={successMessage}
          value={zipCode}
          {...inputOptions}
        />
      </Container>
      <Buttons>
        <CarouselBottomButton
          buttonFormat={bottomButtonFormat}
          carouselItemId={CAROUSEL_ITEM_ID}
          destinationItemId={destinationItemId}
          isDisabled={!isInServiceZone}
        />
      </Buttons>
    </FormGroup>
  );
};
